import { Localization } from '@aa-app-localization'
import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { LocalizationService } from '@app-domains/localization/service/localization.service'
import { CategoryFragment, ContentCategoryFragment, ImageComponentFragment } from '@app-graphql/schema'
import { CategoriesService } from '@app-services'

@Component({
    selector: 'app-product-categories',
    templateUrl: './product-categories.component.html',
    styleUrls: ['./product-categories.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ProductCategoriesComponent implements OnInit {

    @Input()
    public categories: ContentCategoryFragment[]

    @Input()
    public sale: ImageComponentFragment

    @Input()
    public new: ImageComponentFragment

    @Input()
    public title: string

    @Input()
    public subtitle: string | undefined | null

    @Input()
    public lazyLoad: boolean = true

    public gridSize: number

    private possibleCategorySizes = [ 8, 12, 14 ]

    public lengthOfMissingCategoryItems: number[]

    constructor(
        public localizationService: LocalizationService,
        private readonly categoriesService: CategoriesService,
    ) {}

    public ngOnInit(): void {
        this.mergeProductImagesFromCategoryTree()
        this.gridSize = this.getClosestGridSize()
        this.processData()

        // console.log('initial categories', this.categories)
    }

    /**
     * The page `Categories` block often returns `productImage: null` while the same categories
     * in the global `categories` query include images. Copy missing images from the boot-loaded tree.
     */
    private mergeProductImagesFromCategoryTree(): void {
        const tree = this.categoriesService.categories
        if (! tree?.length || ! this.categories?.length) {
            return
        }

        const flat = this.categoriesService.flattenCategories(tree)
        const locale: Localization.Locale = this.localizationService.getCurrentLocale()

        this.categories = this.categories.map((c) => {
            if (c.productImage) {
                return c
            }

            const match =
                flat.find((x) => x.id === c.id)
                ?? this.matchCategoryBySlug(flat, c, locale)

            if (match?.productImage) {
                return { ...c, productImage: match.productImage }
            }

            return c
        })

        // console.log('categories after merge', this.categories)
    }

    private matchCategoryBySlug(
        flat: CategoryFragment[],
        block: ContentCategoryFragment,
        locale: Localization.Locale,
    ): CategoryFragment | undefined {
        const slug = block.translations[locale]?.slug
        if (! slug) {
            return undefined
        }

        return flat.find((x) => x.translations[locale]?.slug === slug)
    }

    private getClosestGridSize(): number {
        return this.possibleCategorySizes.reduce((prev, curr) =>
            Math.abs(curr - this.categories.length) <
                Math.abs(prev - this.categories.length)
                ? curr : prev,
        )
    }

    private processData(): void {
        const differenceInGridItems = this.gridSize - this.categories.length

        if (differenceInGridItems < 0) {
            this.categories = this.categories.slice(0, differenceInGridItems)
            return
        }

        this.lengthOfMissingCategoryItems = Array(differenceInGridItems)
    }

}
